import { parseFormula } from './formula';
import { BY_SYMBOL } from './elements';
import { parseEquation, verifyBalance, type ParsedEquation } from './balance';

/**
 * Redox by the half-reaction method, worked the way it is taught:
 *
 *   assign oxidation numbers → split into half-reactions → balance every atom
 *   except O and H → balance O with water → balance H with H+ → balance charge
 *   with electrons → scale so the electrons cancel → combine and cancel.
 *
 * A basic solution is done in acid first and then neutralised: add OH- to both
 * sides for every H+, turn each H+ + OH- into water, and cancel the water that
 * now appears on both sides. That is the standard route and the one his course
 * will expect to see.
 */

export class RedoxError extends Error {}

export type Medium = 'acid' | 'base';

export interface Term {
  formula: string;
  count: number;
}

export interface HalfReaction {
  kind: 'oxidation' | 'reduction';
  element: string;
  from: number;
  to: number;
  left: Term[];
  right: Term[];
  electrons: number;
  /** Which side the electrons sit on. */
  electronSide: 'left' | 'right';
  steps: string[];
}

export interface RedoxResult {
  ok: true;
  medium: Medium;
  oxidation: HalfReaction;
  reduction: HalfReaction;
  multipliers: { oxidation: number; reduction: number };
  left: Term[];
  right: Term[];
  equation: string;
  steps: string[];
}

export type RedoxOutcome = RedoxResult | { ok: false; message: string };

// ------------------------------------------------------- oxidation numbers

const GROUP_ONE = new Set(['Li', 'Na', 'K', 'Rb', 'Cs', 'Fr']);
const GROUP_TWO = new Set(['Be', 'Mg', 'Ca', 'Sr', 'Ba', 'Ra']);

function isMetal(symbol: string): boolean {
  const category = BY_SYMBOL.get(symbol)?.category;
  return (
    category === 'alkali-metal' ||
    category === 'alkaline-earth' ||
    category === 'transition-metal' ||
    category === 'basic-metal'
  );
}

/**
 * Standard rules, then solve for whatever is left. Enough for the species a
 * General Chemistry course actually uses; where it cannot decide, it says so
 * rather than inventing a number.
 */
export function oxidationNumbers(formula: string): Map<string, number> {
  const parsed = parseFormula(formula);
  const counts = parsed.counts;
  const charge = parsed.charge;
  const symbols = [...counts.keys()];

  if (symbols.length === 1) {
    const only = symbols[0]!;
    const n = counts.get(only)!;
    // A free element is zero; a monatomic ion carries the charge.
    return new Map([[only, n === 0 ? 0 : charge / n]]);
  }

  const fixed = new Map<string, number>();
  const hasOxygen = counts.has('O');
  const peroxide = hasOxygen && (formula === 'H2O2' || /O2\s*2-$/.test(formula));

  for (const symbol of symbols) {
    if (symbol === 'F') fixed.set('F', -1);
    else if (GROUP_ONE.has(symbol)) fixed.set(symbol, 1);
    else if (GROUP_TWO.has(symbol)) fixed.set(symbol, 2);
    else if (symbol === 'O' && !peroxide) fixed.set('O', -2);
    else if (symbol === 'O' && peroxide) fixed.set('O', -1);
    else if (symbol === 'H') {
      // Hydride with a metal, otherwise +1.
      const withMetal = symbols.some((s) => s !== 'H' && isMetal(s));
      fixed.set('H', withMetal && !hasOxygen ? -1 : 1);
    }
  }

  // A halogen is -1 unless it is bonded to oxygen or to something more
  // electronegative. With oxygen present, or with two different halogens, leave
  // it to be solved for rather than assumed.
  const halogens = symbols.filter((s) => s === 'Cl' || s === 'Br' || s === 'I');
  if (!hasOxygen && !symbols.includes('F') && halogens.length === 1) {
    const halogen = halogens[0]!;
    if (!fixed.has(halogen)) fixed.set(halogen, -1);
  }

  const unknown = symbols.filter((s) => !fixed.has(s));
  if (unknown.length === 0) return fixed;
  if (unknown.length > 1) {
    throw new RedoxError(
      `Cannot work out oxidation numbers for ${formula}: more than one element is unaccounted for.`,
    );
  }

  const target = unknown[0]!;
  let sum = 0;
  for (const [symbol, value] of fixed) sum += value * (counts.get(symbol) ?? 0);
  const n = counts.get(target)!;
  fixed.set(target, (charge - sum) / n);
  return fixed;
}

// ------------------------------------------------------- half-reactions

function countsOf(formula: string): Map<string, number> {
  const parsed = parseFormula(formula);
  const out = new Map<string, number>();
  for (const [symbol, n] of parsed.counts) out.set(symbol, n / parsed.coefficient);
  return out;
}

function chargeOf(formula: string): number {
  const parsed = parseFormula(formula);
  return parsed.charge / parsed.coefficient;
}

function gcd(a: number, b: number): number {
  return b === 0 ? Math.abs(a) : gcd(b, a % b);
}
const lcm = (a: number, b: number) => Math.abs(a * b) / (gcd(a, b) || 1);

function tally(terms: Term[], symbol: string): number {
  return terms.reduce((total, term) => total + term.count * (countsOf(term.formula).get(symbol) ?? 0), 0);
}

function tallyCharge(terms: Term[]): number {
  return terms.reduce((total, term) => total + term.count * chargeOf(term.formula), 0);
}

function addTerm(terms: Term[], formula: string, count: number): void {
  if (count <= 0) return;
  const existing = terms.find((t) => t.formula === formula);
  if (existing) existing.count += count;
  else terms.push({ formula, count });
}

function buildHalfReaction(reactant: string, product: string, element: string): HalfReaction {
  const from = oxidationNumbers(reactant).get(element)!;
  const to = oxidationNumbers(product).get(element)!;

  const inReactant = countsOf(reactant).get(element) ?? 0;
  const inProduct = countsOf(product).get(element) ?? 0;
  if (inReactant === 0 || inProduct === 0) {
    throw new RedoxError(`${element} does not appear on both sides of its half-reaction.`);
  }

  const common = lcm(inReactant, inProduct);
  const left: Term[] = [{ formula: reactant, count: common / inReactant }];
  const right: Term[] = [{ formula: product, count: common / inProduct }];

  const steps: string[] = [
    `${element} goes from ${signed(from)} in ${reactant} to ${signed(to)} in ${product}.`,
    `Balance ${element} first: ${describe(left)} → ${describe(right)}.`,
  ];

  // Oxygen with water.
  const oxygenGap = tally(left, 'O') - tally(right, 'O');
  if (oxygenGap !== 0) {
    if (oxygenGap > 0) addTerm(right, 'H2O', oxygenGap);
    else addTerm(left, 'H2O', -oxygenGap);
    steps.push(
      `Balance oxygen with water: add ${Math.abs(oxygenGap)} H2O to the ${oxygenGap > 0 ? 'right' : 'left'}.`,
    );
  }

  // Hydrogen with H+.
  const hydrogenGap = tally(left, 'H') - tally(right, 'H');
  if (hydrogenGap !== 0) {
    if (hydrogenGap > 0) addTerm(right, 'H+', hydrogenGap);
    else addTerm(left, 'H+', -hydrogenGap);
    steps.push(
      `Balance hydrogen with H+: add ${Math.abs(hydrogenGap)} H+ to the ${hydrogenGap > 0 ? 'right' : 'left'}.`,
    );
  }

  // Charge with electrons.
  const chargeGap = tallyCharge(left) - tallyCharge(right);
  const electrons = Math.abs(chargeGap);
  const electronSide: 'left' | 'right' = chargeGap > 0 ? 'left' : 'right';
  if (electrons !== 0) {
    steps.push(
      `Balance the charge with electrons: the ${electronSide} is ${signed(chargeGap)} out, so add ${electrons} e- to the ${electronSide}.`,
    );
  }

  const kind = electronSide === 'left' ? 'reduction' : 'oxidation';
  steps.push(
    `That makes this the ${kind} half: ${describeWithElectrons(left, right, electrons, electronSide)}.`,
  );

  return { kind, element, from, to, left, right, electrons, electronSide, steps };
}

const signed = (n: number) => (n > 0 ? `+${n}` : String(n));

function describe(terms: Term[]): string {
  return terms.map((t) => (t.count === 1 ? t.formula : `${t.count} ${t.formula}`)).join(' + ');
}

function describeWithElectrons(
  left: Term[],
  right: Term[],
  electrons: number,
  side: 'left' | 'right',
): string {
  const l = describe(left) + (side === 'left' && electrons ? ` + ${electrons} e-` : '');
  const r = describe(right) + (side === 'right' && electrons ? ` + ${electrons} e-` : '');
  return `${l} → ${r}`;
}

// ---------------------------------------------------------------- solving

/** Finds the element whose oxidation number changes between two species. */
function changedElements(
  reactants: string[],
  products: string[],
): Array<{ element: string; reactant: string; product: string; from: number; to: number }> {
  const out: Array<{ element: string; reactant: string; product: string; from: number; to: number }> = [];

  for (const reactant of reactants) {
    let numbersR: Map<string, number>;
    try {
      numbersR = oxidationNumbers(reactant);
    } catch {
      continue;
    }
    for (const [element, from] of numbersR) {
      for (const product of products) {
        let numbersP: Map<string, number>;
        try {
          numbersP = oxidationNumbers(product);
        } catch {
          continue;
        }
        const to = numbersP.get(element);
        if (to === undefined || to === from) continue;
        out.push({ element, reactant, product, from, to });
      }
    }
  }

  // Prefer elements other than O and H: those are the ones actually changing in
  // a textbook problem, and water dragged in as a spectator would confuse it.
  const interesting = out.filter((c) => c.element !== 'O' && c.element !== 'H');
  return interesting.length > 0 ? interesting : out;
}

export function balanceRedox(input: string, medium: Medium = 'acid'): RedoxOutcome {
  let parsed: ParsedEquation;
  try {
    parsed = parseEquation(input);
  } catch (err) {
    return { ok: false, message: (err as Error).message };
  }

  let changes: ReturnType<typeof changedElements>;
  try {
    changes = changedElements(parsed.reactants, parsed.products);
  } catch (err) {
    return { ok: false, message: (err as Error).message };
  }

  const oxidations = changes.filter((c) => c.to > c.from);
  const reductions = changes.filter((c) => c.to < c.from);

  if (oxidations.length === 0 || reductions.length === 0) {
    return {
      ok: false,
      message:
        'This is not a redox reaction as written — no element both gains and loses electrons. Use the ordinary balancer.',
    };
  }

  const ox = oxidations[0]!;
  const red = reductions[0]!;

  let oxidation: HalfReaction;
  let reduction: HalfReaction;
  try {
    oxidation = buildHalfReaction(ox.reactant, ox.product, ox.element);
    reduction = buildHalfReaction(red.reactant, red.product, red.element);
  } catch (err) {
    return { ok: false, message: (err as Error).message };
  }

  if (oxidation.electrons === 0 || reduction.electrons === 0) {
    return { ok: false, message: 'One of the half-reactions needs no electrons, which cannot be right.' };
  }

  // Scale so the electrons cancel.
  const electronTotal = lcm(oxidation.electrons, reduction.electrons);
  const oxFactor = electronTotal / oxidation.electrons;
  const redFactor = electronTotal / reduction.electrons;

  const left: Term[] = [];
  const right: Term[] = [];
  for (const term of oxidation.left) addTerm(left, term.formula, term.count * oxFactor);
  for (const term of oxidation.right) addTerm(right, term.formula, term.count * oxFactor);
  for (const term of reduction.left) addTerm(left, term.formula, term.count * redFactor);
  for (const term of reduction.right) addTerm(right, term.formula, term.count * redFactor);

  const steps: string[] = [
    'Work out the oxidation number of every element to see what is oxidised and what is reduced.',
    `${ox.element} rises from ${signed(ox.from)} to ${signed(ox.to)}, so it is oxidised. ${red.element} falls from ${signed(red.from)} to ${signed(red.to)}, so it is reduced.`,
    ...oxidation.steps.map((s) => `Oxidation half — ${s}`),
    ...reduction.steps.map((s) => `Reduction half — ${s}`),
    `Equalise the electrons: ${oxidation.electrons} and ${reduction.electrons} both divide into ${electronTotal}, so multiply the oxidation half by ${oxFactor} and the reduction half by ${redFactor}.`,
    'Add the two halves together; the electrons cancel exactly.',
  ];

  cancelCommon(left, right, steps);

  if (medium === 'base') {
    toBasic(left, right, steps);
  }

  const finalEquation = `${describe(left)} → ${describe(right)}`;
  steps.push(`Balanced: ${finalEquation}`);

  return {
    ok: true,
    medium,
    oxidation,
    reduction,
    multipliers: { oxidation: oxFactor, reduction: redFactor },
    left,
    right,
    equation: finalEquation,
    steps,
  };
}

/** Removes whatever appears on both sides, which is how H2O and H+ tidy up. */
function cancelCommon(left: Term[], right: Term[], steps: string[]): void {
  const cancelled: string[] = [];

  for (const term of [...left]) {
    const match = right.find((t) => t.formula === term.formula);
    if (!match) continue;
    const shared = Math.min(term.count, match.count);
    if (shared === 0) continue;
    term.count -= shared;
    match.count -= shared;
    cancelled.push(`${shared} ${term.formula}`);
  }

  for (let i = left.length - 1; i >= 0; i -= 1) if (left[i]!.count === 0) left.splice(i, 1);
  for (let i = right.length - 1; i >= 0; i -= 1) if (right[i]!.count === 0) right.splice(i, 1);

  if (cancelled.length > 0) {
    steps.push(`Cancel what appears on both sides: ${cancelled.join(', ')}.`);
  }
}

/** Neutralises the acid form: OH- on both sides, H+ + OH- becomes water. */
function toBasic(left: Term[], right: Term[], steps: string[]): void {
  const side = left.find((t) => t.formula === 'H+') ? left : right;
  const other = side === left ? right : left;
  const protons = side.find((t) => t.formula === 'H+');
  if (!protons || protons.count === 0) {
    steps.push('There is no H+ left, so the equation is already fit for basic solution.');
    return;
  }

  const n = protons.count;
  steps.push(
    `The solution is basic, so neutralise the H+: add ${n} OH- to both sides. On the ${side === left ? 'left' : 'right'} the ${n} H+ and ${n} OH- become ${n} H2O.`,
  );

  // H+ + OH- -> H2O on the side that had the protons.
  side.splice(side.indexOf(protons), 1);
  addTerm(side, 'H2O', n);
  // The other side just gains the hydroxide.
  addTerm(other, 'OH-', n);

  cancelCommon(left, right, steps);
}

/** Checks a redox result really does balance, atoms and charge. */
export function verifyRedox(result: RedoxResult): boolean {
  const expand = (terms: Term[]) => terms.flatMap((t) => Array(t.count).fill(t.formula) as string[]);
  const parsed: ParsedEquation = { reactants: expand(result.left), products: expand(result.right) };
  const coefficients = [...parsed.reactants, ...parsed.products].map(() => 1n);
  return verifyBalance(parsed, coefficients).balanced;
}
